import React from "react";

type StatsTilesIconProps = {
    className?: string;
    size?: number;
    strokeWidth?: number;
    "aria-hidden"?: boolean;
};

export type StatsTilesIconComponent = React.ElementType<StatsTilesIconProps>;

export type StatsTilesIcon = React.ReactElement | string | number | StatsTilesIconComponent;

export type StatsTilesItem = {
    id?: string | number;
    value: React.ReactNode;
    label?: React.ReactNode;
    description?: React.ReactNode;
    icon?: StatsTilesIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    mutedTextColor?: string;
    headingTextShadow?: string;
    ariaLabel?: string;
};

export type StatsTilesProps = {
    items: StatsTilesItem[];
    accentColor?: string;
    backgroundColor?: string;
    tileBackgroundColor?: string;
    textColor?: string;
    mutedTextColor?: string;
    borderColor?: string;
    headingTextShadow?: string;
    maxWidth?: number | string;
    minTileWidth?: number | string;
    className?: string;
    gridClassName?: string;
    tileClassName?: string;
    ariaLabel?: string;
    as?: "section" | "div";
};

type StatsTilesStyle = React.CSSProperties & Record<`--${string}`, string | undefined>;

const statsTilesCss = `
.stats-tiles {
  --stats-tiles-bg: transparent;
  --stats-tiles-accent: #e50914;
  --stats-tiles-card-bg: linear-gradient(135deg, rgba(38, 38, 38, 0.98) 0%, rgba(21, 22, 22, 0.98) 100%);
  --stats-tiles-text: #ffffff;
  --stats-tiles-muted: rgba(255, 255, 255, 0.68);
  --stats-tiles-border: rgba(255, 255, 255, 0.06);
  --stats-tiles-heading-shadow: 1px 2px 0 rgba(0, 0, 0, 0.72);
  --stats-tiles-max-width: 1440px;
  --stats-tiles-min-width: 280px;
  width: 100%;
  background: var(--stats-tiles-bg);
  color: var(--stats-tiles-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.stats-tiles,
.stats-tiles * {
  box-sizing: border-box;
}
.stats-tiles-shell {
  width: 100%;
  max-width: var(--stats-tiles-max-width);
  margin: 0 auto;
}
.stats-tiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--stats-tiles-min-width)), 1fr));
  gap: 18px;
  width: 100%;
}
.stats-tiles-card {
  --stats-tiles-tile-accent: var(--stats-tiles-accent);
  --stats-tiles-tile-bg: var(--stats-tiles-card-bg);
  --stats-tiles-tile-text: var(--stats-tiles-text);
  --stats-tiles-tile-muted: var(--stats-tiles-muted);
  position: relative;
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  min-width: 0;
  min-height: 136px;
  align-items: center;
  gap: 26px;
  overflow: hidden;
  border: 1px solid var(--stats-tiles-border);
  border-radius: 8px;
  background: var(--stats-tiles-tile-bg);
  color: var(--stats-tiles-tile-text);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 18px 34px rgba(0, 0, 0, 0.22);
  padding: 26px 30px;
  isolation: isolate;
}
.stats-tiles-card--no-icon {
  grid-template-columns: minmax(0, 1fr);
}
.stats-tiles-card::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(120deg, rgba(255, 255, 255, 0.055), transparent 44%),
    radial-gradient(circle at 24% 0%, color-mix(in srgb, var(--stats-tiles-tile-accent) 18%, transparent), transparent 38%);
  opacity: 0.92;
}
.stats-tiles-icon {
  display: inline-flex;
  width: 92px;
  height: 92px;
  min-width: 0;
  align-items: center;
  justify-content: center;
  color: var(--stats-tiles-tile-accent);
  filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.58));
}
.stats-tiles-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.stats-tiles-custom-icon {
  display: inline-flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
}
.stats-tiles-icon svg,
.stats-tiles-custom-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  stroke: currentColor;
}
.stats-tiles-content {
  min-width: 0;
}
.stats-tiles-value,
.stats-tiles-label {
  max-width: 100%;
  margin: 0;
  color: var(--stats-tiles-tile-text);
  font-family: Impact, Haettenschweiler, "Arial Narrow Bold", "Roboto Condensed", Inter, sans-serif;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
  text-shadow: var(--stats-tiles-heading-shadow);
  overflow-wrap: anywhere;
  white-space: pre-line;
}
.stats-tiles-value {
  font-size: 34px;
  line-height: 1.02;
}
.stats-tiles-label {
  margin-top: 6px;
  font-size: 22px;
  line-height: 1.05;
}
.stats-tiles-description {
  max-width: 100%;
  margin: 13px 0 0;
  color: var(--stats-tiles-tile-muted);
  font-size: 15px;
  line-height: 1.35;
  font-weight: 760;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
@supports not (background: color-mix(in srgb, red 10%, transparent)) {
  .stats-tiles-card::before {
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.055), transparent 44%);
  }
}
@media (max-width: 860px) {
  .stats-tiles-grid {
    gap: 14px;
  }
  .stats-tiles-card {
    grid-template-columns: 78px minmax(0, 1fr);
    min-height: 126px;
    gap: 22px;
    padding: 22px 24px;
  }
  .stats-tiles-icon {
    width: 78px;
    height: 78px;
  }
  .stats-tiles-value {
    font-size: 30px;
  }
  .stats-tiles-label {
    font-size: 19px;
  }
  .stats-tiles-description {
    font-size: 14px;
  }
}
@media (max-width: 480px) {
  .stats-tiles-grid {
    gap: 12px;
  }
  .stats-tiles-card {
    grid-template-columns: 64px minmax(0, 1fr);
    min-height: 118px;
    gap: 17px;
    padding: 20px 18px;
  }
  .stats-tiles-icon {
    width: 64px;
    height: 64px;
  }
  .stats-tiles-value {
    font-size: 27px;
  }
  .stats-tiles-label {
    font-size: 17px;
  }
  .stats-tiles-description {
    margin-top: 10px;
    font-size: 13px;
  }
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(" ");

const getCssSize = (value?: number | string) => {
    if (typeof value === "number") {
        return `${value}px`;
    }

    return value;
};

const hasContent = (value: React.ReactNode) =>
    value !== undefined && value !== null && value !== false && value !== "";

const renderIcon = (tile: StatsTilesItem) => {
    if (tile.iconSrc) {
        return <img src={tile.iconSrc} alt={tile.iconAlt || ""} loading="lazy" />;
    }

    if (!tile.icon) {
        return null;
    }

    if (React.isValidElement(tile.icon)) {
        return <span className="stats-tiles-custom-icon">{tile.icon}</span>;
    }

    if (typeof tile.icon === "string" || typeof tile.icon === "number") {
        return <span className="stats-tiles-custom-icon">{tile.icon}</span>;
    }

    if (tile.icon) {
        const IconComponent = tile.icon as StatsTilesIconComponent;

        return (
            <IconComponent
                className="stats-tiles-lucide-icon"
                size={78}
                strokeWidth={1.8}
                aria-hidden
            />
        );
    }
};

const StatsTiles = ({
    items,
    accentColor = "#e50914",
    backgroundColor,
    tileBackgroundColor,
    textColor,
    mutedTextColor,
    borderColor,
    headingTextShadow,
    maxWidth,
    minTileWidth,
    className,
    gridClassName,
    tileClassName,
    ariaLabel,
    as = "section",
}: StatsTilesProps) => {
    if (!items.length) {
        return null;
    }

    const RootTag = as;
    const rootStyle: StatsTilesStyle = {
        "--stats-tiles-accent": accentColor,
        "--stats-tiles-bg": backgroundColor,
        "--stats-tiles-card-bg": tileBackgroundColor,
        "--stats-tiles-text": textColor,
        "--stats-tiles-muted": mutedTextColor,
        "--stats-tiles-border": borderColor,
        "--stats-tiles-heading-shadow": headingTextShadow,
        "--stats-tiles-max-width": getCssSize(maxWidth),
        "--stats-tiles-min-width": getCssSize(minTileWidth),
    };

    return (
        <RootTag
            className={joinClassNames("stats-tiles", className)}
            style={rootStyle}
            aria-label={ariaLabel}
        >
            <style>{statsTilesCss}</style>

            <div className="stats-tiles-shell">
                <div className={joinClassNames("stats-tiles-grid", gridClassName)}>
                    {items.map((item, index) => {
                        const tileStyle: StatsTilesStyle = {
                            "--stats-tiles-tile-accent": item.accentColor,
                            "--stats-tiles-tile-bg": item.backgroundColor,
                            "--stats-tiles-tile-text": item.textColor,
                            "--stats-tiles-tile-muted": item.mutedTextColor,
                            "--stats-tiles-heading-shadow": item.headingTextShadow,
                        };
                        const icon = renderIcon(item);
                        const showLabel = hasContent(item.label);
                        const showDescription = hasContent(item.description);

                        return (
                            <article
                                key={item.id || index}
                                className={joinClassNames(
                                    "stats-tiles-card",
                                    !icon && "stats-tiles-card--no-icon",
                                    tileClassName
                                )}
                                style={tileStyle}
                                aria-label={item.ariaLabel}
                            >
                                {icon ? (
                                    <span
                                        className="stats-tiles-icon"
                                        aria-hidden={item.iconAlt ? undefined : true}
                                    >
                                        {icon}
                                    </span>
                                ) : null}

                                <div className="stats-tiles-content">
                                    <p className="stats-tiles-value">{item.value}</p>

                                    {showLabel ? (
                                        <p className="stats-tiles-label">{item.label}</p>
                                    ) : null}

                                    {showDescription ? (
                                        <p className="stats-tiles-description">
                                            {item.description}
                                        </p>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </RootTag>
    );
};

export default StatsTiles;
